import React, {useEffect, useState} from 'react';
import axios from "axios";
import {useParams} from "react-router-dom";
import {enqueueSnackbar} from "notistack";
import {Loading} from "@/layouts/buddy/loading";

interface IJobSummaryItem {
    number: string;
    label: string;
    onClick?: () => void;
}

interface IJobSummaryItems extends Array<IJobSummaryItem> {
}

interface IJobSummary {
    active_candidates: number;
}

interface JobSummaryProps {
    setActivePage: (page: string) => void;
    onSummaryFetched?: (activeCandidates: number) => void;
}

const JobSummary: React.FC<JobSummaryProps> = ({setActivePage, onSummaryFetched}) => {
    const {p_id} = useParams(); // Access `p_id` from URL

    const [items, setItems] = useState<IJobSummaryItems>([]);
    const [loading, setLoading] = useState<boolean>(true);

    const API_URL = import.meta.env.VITE_APP_API_URL;

    useEffect(() => {
        if (!p_id) {
            enqueueSnackbar(`No job ID provided.`, {
                variant: 'solid',
                state: 'danger'
            });
            setLoading(false);
            return;
        }

        const fetchJobSummary = async () => {
            try {
                const token = localStorage.getItem('authToken'); // Adjust as needed

                const response = await axios.get<IJobSummary>(`${API_URL}/job/${p_id}/summary`, {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                    },
                });

                const {active_candidates} = response.data;

                if (onSummaryFetched) {
                    onSummaryFetched(active_candidates);
                }

                console.log(response.data)

                const JobSummaryItems: IJobSummaryItems = [
                    {
                        number: active_candidates.toString(),
                        label: 'Matched Candidates',
                        onClick: () => setActivePage('candidates'),
                    }
                ];

                setItems(JobSummaryItems);
                setLoading(false);
            } catch (err: any) {
                console.error('Error fetching job summary:', err);
                if (err.response && err.response.status === 404) {
                    enqueueSnackbar(`Job not found or access denied: ${err}`, {
                        variant: 'solid',
                        state: 'danger'
                    });
                } else if (err.response && err.response.status === 401) {
                    enqueueSnackbar(`Unauthorized. Please log in: ${err}`, {
                        variant: 'solid',
                        state: 'danger'
                    });
                } else {
                    enqueueSnackbar(`Failed to fetch job summary: ${err}`, {
                        variant: 'solid',
                        state: 'danger'
                    });
                }
                setLoading(false);
            }
        };

        fetchJobSummary();
    }, [API_URL, p_id, setActivePage, onSummaryFetched]);

    const renderItems = (item: IJobSummaryItem, index: number) => {
        return (
            <React.Fragment key={index}>
                <button
                    onClick={item.onClick}
                    className={`flex flex-col items-center flex-1 gap-1 text-center focus:outline-none 
                           ${item.onClick ? 'cursor-pointer' : ''}`}
                >
                <span className="text-gray-900 text-2xl lg:text-3xl leading-none font-semibold hover:text-primary">
                    {item.number}
                </span>
                    <span className="text-gray-700 text-sm hover:text-primary">
                    {item.label}
                </span>
                </button>
                {index !== items.length - 1 && (
                    <span className="border-r border-r-gray-300 my-1 h-full"></span>
                )}
            </React.Fragment>
        );
    };

    if (loading) return <Loading loading={loading}/>;

    return (
        <div className="card">
            <div className="card-body">
                <div className="flex lg:px-10 py-1.5 gap-2">
                    {items.map((item, index) => renderItems(item, index))}
                </div>
            </div>
        </div>
    );
};

export {JobSummary, type IJobSummaryItem, type IJobSummaryItems};
